import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SimpleLeaderboard from '../../components/common/SimpleLeaderboard';
import SimplePlacesList from '../../components/common/SimplePlacesList';
import { useAuth } from '../../contexts/AuthContext';
import { fetchPosts } from '../../services/postService';
import LocalMap from '../LocalMap';
import './MainPage.css';

export default function MainPage() {
  const mapSearchRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [postsError, setPostsError] = useState('');
  const [tripDrawerOpen, setTripDrawerOpen] = useState(false);
  const [draftStops, setDraftStops] = useState([]);
  const [tripCost, setTripCost] = useState('');
  const [transportMode, setTransportMode] = useState('car');
  const [tripNotes, setTripNotes] = useState('');

  // Load featured posts on mount
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setPostsError('');
        setLoadingPosts(true);
        const posts = await fetchPosts(1, 6, null);
        if (active) setFeaturedPosts(posts || []);
      } catch (err) {
        if (active) setPostsError('Unable to load posts right now.');
      } finally {
        if (active) setLoadingPosts(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const parseHashtags = (text) => {
    return [...(text || '').matchAll(/#([\w-]+)/g)].map(m => (m[1] || '').toLowerCase()).filter(Boolean);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (typeof mapSearchRef.current === 'function') {
      mapSearchRef.current(query);
    }
  };

  const handleAddStop = (spot) => {
    if (!spot) return;
    setTripDrawerOpen(true);
    setDraftStops((prev) => {
      const exists = prev.some((p) => (p.id || p.name) === (spot.id || spot.name));
      if (exists) return prev;
      return [...prev, { id: spot.id, name: spot.name, lat: spot.lat, lng: spot.lng, category: spot.category }];
    });
    if (typeof mapSearchRef.current === 'function') {
      if (spot.lat != null && spot.lng != null) {
        mapSearchRef.current({ lat: spot.lat, lng: spot.lng });
      } else if (spot.name) {
        mapSearchRef.current(spot.name);
      }
    }
  };

  const handleRemoveStop = (idx) => {
    setDraftStops((prev) => prev.filter((_, i) => i !== idx));
  };

  const moveStop = (from, to) => {
    setDraftStops((prev) => {
      if (to < 0 || to >= prev.length) return prev;
      const arr = prev.slice();
      const [item] = arr.splice(from, 1);
      arr.splice(to, 0, item);
      return arr;
    });
  };

  const handleCreateTripNow = () => {
    setTripDrawerOpen(true);
    if (draftStops.length === 0 && typeof mapSearchRef.current === 'function') {
      // Nudge user to the default center
      mapSearchRef.current([0, 20]);
    }
  };

  return (
    <div className="mainpage">
    <section className="top-hero">
      <h1 className="top-title">Welcome aboard</h1>
      <p className="top-subtitle">discover places, plan routes, and share your journey.</p>

      <form className="top-search" onSubmit={onSubmit} autoComplete="off">
        <input
          type="search"
          placeholder="Search places, #tags, or addresses..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="top-search-input"
        />
        <button className="top-search-btn" type="submit">Search</button>
      </form>

      <div className="top-map-row">
        {/* Left: Leaderboards */}
        <aside className="top-panel">
          <div className="panel-card">
            <div className="panel-header">
              <h4 className="panel-title">Leader Boards</h4>
            </div>
            <div className="panel-body">
              <SimpleLeaderboard limit={5} timeframe="all" />
            </div>
          </div>
        </aside>

        {/* Center: Map */}
        <div className="top-map">
          <LocalMap
            setSearchHandler={mapSearchRef}
            compact
            initialCenter={[0, 20]}
            initialZoom={1.7}
            minZoom={1}
          />

          <button className="top-map-cta" onClick={() => navigate('/localmap')}>
            🔍 Start with Map
          </button>
        </div>

        {/* Right: Places to go */}
        <aside className="top-panel">
          <div className="panel-card">
            <div className="panel-header panel-header-split">
              <h4 className="panel-title">Places to go</h4>
              <button className="panel-link" onClick={() => navigate('/feed')}>View</button>
            </div>
            <div className="panel-body">
              <SimplePlacesList
                limit={6}
                onSelect={(spot) => handleAddStop(spot)}
              />
            </div>
          </div>
        </aside>
      </div>

      {/* 4 feature cards row (under map) */}
      <div className="top-feature-row">
        <button className="top-feature-card" onClick={() => navigate('/localmap')}>
          <img className="top-feature-icon" src="/gem-icon.png" alt="Discover" />
          <div className="top-feature-title">Discover</div>
          <div className="top-feature-text">Find places and plan routes with an interactive map</div>
        </button>

        <button className="top-feature-card" onClick={() => navigate('/feed')}>
          <img className="top-feature-icon" src="/star-icon.png" alt="Things to do" />
          <div className="top-feature-title">Things to do</div>
          <div className="top-feature-text">Browse posts with spots, tips, and curated lists</div>
        </button>

        <button className="top-feature-card" onClick={() => navigate('/find-people')}>
          <img className="top-feature-icon" src="/flower-icon.png" alt="Find Pioneers" />
          <div className="top-feature-title">Find Pioneers</div>
          <div className="top-feature-text">Follow explorers and build your travel circle</div>
        </button>

        <button className="top-feature-card" onClick={() => navigate('/leaderboards')}>
          <img className="top-feature-icon" src="/popcorn-icon.png" alt="Compete" />
          <div className="top-feature-title">Compete</div>
          <div className="top-feature-text">See top contributors and trending places</div>
        </button>
      </div>
    </section>
    
    {/* ===== PLAN ROUTES (compact strip) ===== */}
    <section className="planroutes">
      <div className="planroutes-shell">
        <div className="planroutes-header">
          <h3 className="planroutes-title">Plan Routes</h3>
        </div>

        <div className="planroutes-grid">
          {/* Left mini map preview */}
          <div className="planroutes-card map-preview" onClick={() => navigate('/localmap')}>
            <div className="planroutes-miniMap">
              <div className="planroutes-miniMapInner">
                <LocalMap
                  compact
                  initialCenter={[0, 20]}
                  initialZoom={1.7}
                  minZoom={1}
                  interactive={false}
                />
              </div>
              <div className="miniMap-pin" aria-hidden="true" />
            </div>
          </div>

          {/* Plan Your Trips */}
          <div className="planroutes-card plan-trip">
            <div className="planroutes-card-header">
              <h4>Plan Your Trips</h4>
            </div>

            <div className="route-panel">
              <div className="route-panel-title">🧭 Route Path</div>

              <div className="route-panel-grid">
                <div className="route-row">
                  <div className="route-label">Transport Mode</div>
                  <div className="route-value">{transportMode === 'car' ? 'Driving 🚗' : transportMode}</div>
                </div>
                <div className="route-row">
                  <div className="route-label">Stops</div>
                  <div className="route-value">{draftStops.length || 0}</div>
                </div>
                <div className="route-row">
                  <div className="route-label">Trip Cost</div>
                  <div className="route-value">{tripCost || '—'}</div>
                </div>
              </div>

              <div className="route-actions">
                <button className="route-btn primary" onClick={handleCreateTripNow}>Build Route</button>
                <button className="route-btn" onClick={() => setTripDrawerOpen(true)}>Open Trip</button>
              </div>
            </div>
          </div>

          {/* Share your journey */}
          <div className="planroutes-card share-trip">
            <div className="planroutes-card-header">
              <h4>Share your journey</h4>
            </div>

            <div className="share-panel">
              <div className="share-label">Invite friends</div>

              <div className="share-row">
                <button className="share-chip">Public</button>
                <button className="share-chip">Friends</button>
                <button className="share-chip">Private</button>
              </div>

              <div className="share-actions">
                <button className="route-btn primary" onClick={() => navigate('/feed')}>Share Post</button>
                <button
                  className="route-btn"
                  onClick={() => navigator.clipboard?.writeText(window.location.href)}
                >
                  Copy Link
                </button>
              </div>
            </div>
          </div>

          {/* All Routes */}
          <div className="planroutes-card all-routes">
            <div className="planroutes-card-header header-split">
              <h4>All Routes</h4>
              <button className="mini-link" onClick={() => navigate('/routes')}>View</button>
            </div>

            <div className="routes-list">
              <div className="route-pill">
                <span className="dot red" />
                <div className="pill-text">
                  <div className="pill-title">Seattle</div>
                  <div className="pill-sub">4 stops</div>
                </div>
                <button className="pill-btn">Open</button>
              </div>

              <div className="route-pill">
                <span className="dot green" />
                <div className="pill-text">
                  <div className="pill-title">Davis</div>
                  <div className="pill-sub">3 stops</div>
                </div>
                <button className="pill-btn">Open</button>
              </div>

              <div className="route-pill">
                <span className="dot blue" />
                <div className="pill-text">
                  <div className="pill-title">NYC</div>
                  <div className="pill-sub">5 stops</div>
                </div>
                <button className="pill-btn">Open</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>


    <section className="landing-section explore-section">
      <div className="section-header">
        <h3 className="section-title">Explore</h3>
        <button className="section-cta" onClick={() => navigate('/feed')}>View All</button>
      </div>

      <div className="explore-list">
        {(featuredPosts || []).slice(0, 6).map((post) => {
          const lat = post.lat ?? post.latitude ?? post.location?.lat;
          const lng = post.lng ?? post.longitude ?? post.location?.lng;

          const mapCenter =
            lat != null && lng != null
              ? [lng, lat]
              : [0, 20];

          return (
            <div key={post.id} className="explore-row">
              {/* LEFT: Post card */}
              <article
                className="explore-post-card"
                onClick={() => navigate('/feed')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && navigate('/feed')}
              >
                <div
                  className="explore-post-image"
                  style={{ backgroundImage: `url(${post.image_url || '/promoted.webp'})` }}
                />
                <div className="explore-post-body">
                  <div className="explore-post-title">{post.title || 'Untitled Post'}</div>
                  <div className="explore-post-meta">
                    <span className="explore-post-author">{post.user?.username || 'Anonymous'}</span>
                    <span className="explore-post-stats">
                      ❤️ {post.likes_count || 0} · 💬 {post.comments_count || 0}
                    </span>
                  </div>
                </div>
              </article>
              <div className="explore-map-card" onClick={() => navigate('/localmap')} role="button" tabIndex={0}>
                <div className="explore-map-wrap">
                  <LocalMap
                    compact
                    initialCenter={mapCenter}
                    initialZoom={lat != null && lng != null ? 12 : 1.7}
                    minZoom={1}
                    interactive={false}
                  />
                  <div className="explore-map-pin" aria-hidden="true" />
                </div>

                <div className="explore-map-footer">
                  <div className="explore-map-label">
                    {post.place_name || post.location_name || post.address || 'Open on map'}
                  </div>
                  <button className="text-link" onClick={(e) => { e.stopPropagation(); navigate('/localmap'); }}>
                    View map
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {(featuredPosts || []).length === 0 && !loadingPosts && !postsError && (
          <div className="posts-empty">No posts yet. Be the first to share!</div>
        )}
      </div>
    </section>

    </div>
  );
}
